import React from "react";
import { adminRpcDataset } from "../../model/qubes/dataset";
import { createForm } from "./dataset-form";
import { JobDef } from "./job-model";
import { PayloadControl } from "./renderer/payload-control";
import { daemonIssues, isDaemonKind, runAsIssue, toPayload } from "./renderer/payload-model";
import { knownDaemonCapability } from "./renderer/daemon-capability";
import { ValidationIssue } from "./validation";
import { LookupCache } from "../../services/lookup-cache";

const WORKERS = "continuum:workers";
const FILESETS = "continuum:filesets";

// Register the reference lookups the worker/fileset selects need. Idempotent.
function registerJobLookups() {
  LookupCache.register({
    key: WORKERS,
    sql: "from worker w join server s on s.uid = w.serveruid select w.uid, w.login || '@' || s.name as label",
    valueField: "uid",
    labelField: "label",
  });
  LookupCache.register({
    key: FILESETS,
    sql: "from fileset select uid, name",
    valueField: "uid",
    labelField: "name",
  });
}

const jobDataset = adminRpcDataset({
  cube: "job",
  model: JobDef,
  jsonColumns: [JobDef.payload_json],
});

// The whole-form validator (submit-first; the renderer also runs field validators).
function validateJob(v: Record<string, unknown>): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (!String(v["name"] ?? "").trim()) {
    issues.push({ severity: "error", fields: ["name"], message: "Name is required." });
  }
  if (!v["workeruid"]) {
    issues.push({ severity: "error", fields: ["workeruid"], message: "A worker is required." });
  }
  const attempts = Number(v["max_attempts"]);
  if (!Number.isFinite(attempts) || attempts < 1) {
    issues.push({ severity: "error", fields: ["max_attempts"], message: "Max attempts must be at least 1." });
  }
  // A systemd-transient job with no run-as user is rejected by the privileged executor
  // fail-closed (godev pkg/jobexecutor/policy.go), so catch it here rather than at launch.
  const payload = toPayload(v[JobDef.payload_json.name]);
  const runAs = runAsIssue(payload);
  if (runAs) {
    issues.push({ severity: "error", fields: [JobDef.payload_json.name], message: runAs });
  }
  // What a daemon cannot run without. The control pins the backend, but the form is not
  // the only way a payload reaches here — an edited job carries whatever it was saved with.
  const kind = v[JobDef.kind.name];
  for (const message of daemonIssues(kind, payload)) {
    issues.push({ severity: "error", fields: [JobDef.payload_json.name], message });
  }
  // A daemon on a runner with no executor is accepted and then never starts. Only a
  // KNOWN "no" blocks: an answer that has not arrived is not evidence, and the runner is
  // the authority either way (see daemon-capability.ts).
  if (isDaemonKind(kind) && knownDaemonCapability(v[JobDef.worker_uid.name] as string) === false) {
    issues.push({
      severity: "error",
      fields: [JobDef.worker_uid.name],
      message:
        "This host's job runner is not daemon-capable — it has no job executor, so a daemon job here would never start.",
    });
  }
  return issues;
}

// ── Setting up the form = one call; fields referenced by object ─────────────
export function showJobForm(jobUid?: string) {
  registerJobLookups();
  createForm(jobDataset, {
    key: jobUid, // undefined = create a new job
    title: jobUid ? "Edit job" : "New job",
    submitLabel: "Save job",
    sections: [
      { title: "Identity", fields: [JobDef.name, JobDef.description, JobDef.kind] },
      { title: "Target", fields: [JobDef.worker_uid, JobDef.fileset_uid] },
      { title: "What to run", fields: [JobDef.payload_json] },
      { title: "Retry", fields: [JobDef.max_attempts] },
      { title: "Logging", fields: [JobDef.logparser] },
    ],
    overrides: [
      {
        field: JobDef.worker_uid,
        item: (i) => ({ ...i, type: "select", options: () => LookupCache.getOptions(WORKERS) }),
      },
      {
        field: JobDef.fileset_uid,
        item: (i) => ({ ...i, type: "select", options: () => LookupCache.getOptions(FILESETS) }),
      },
      {
        field: JobDef.payload_json,
        item: (i) => ({
          ...i,
          render: (ctx) =>
            React.createElement(PayloadControl, {
              value: ctx.value,
              onChange: ctx.onChange,
              // The Run as picker offers the accounts on the job's HOST, so it needs the
              // worker the form currently has selected — not the saved one.
              workerUid: ctx.values[JobDef.worker_uid.name] as string | undefined,
              // The kind lives on the job row, not in the payload, but it decides what the
              // payload must carry — so the control is told which kind it is editing for.
              kind: ctx.values[JobDef.kind.name] as string | undefined,
            }),
        }),
      },
      {
        field: JobDef.logparser,
        item: (i) => ({
          ...i,
          helpText:
            "Default parser the log viewer uses for this job's logs. " +
            "systemd journal and Windows Event Log are auto-detected regardless.",
        }),
      },
    ],
    validate: validateJob,
  });
}
